/**
 * La fiche d'un enfant, telle que l'équipe la corrige (Gestion › Enfants › Modifier).
 *
 * ## Les données des parents, plus les anciennes colonnes (26/10/2026)
 *
 * L'écran avait gardé les champs d'avant le formulaire des parents :
 *
 * - une case « Consentement photo » qui écrivait `kids.photo_consent`, alors
 *   que les parents répondent à cinq questions (`kid_image_rights`) et que
 *   `photo_consent` n'en est plus qu'un miroir tenu par un déclencheur — la
 *   case contredisait les cinq réponses, et la prochaine écriture du parent
 *   l'écrasait ;
 * - l'accord de soins rangé dans la santé, sans son texte ;
 * - « Sait nager » à côté d'un niveau en texte libre, quand le parent choisit
 *   l'un de quatre niveaux d'où `can_swim` se déduit ;
 * - un accompagnement dédié et une heure de départ en texte libre ;
 * - aucune personne autorisée ;
 * - les seules écoles actives, sans « autre école » : une école tapée à la
 *   main ou désactivée s'affichait « Aucune école ».
 *
 * L'écran lit et écrit désormais le modèle des parents (`Fiche`,
 * `depuisLignes`, `ecrituresDeLEtape`) : mêmes questions, mêmes colonnes,
 * mêmes règles. Ce module n'ajoute que ce qui change pour l'équipe.
 *
 * ## L'équipe ne crée pas d'erreur, et ne comble pas les anciens manques
 *
 * Près de 3 000 fiches datent d'avant ces questions : pas de médecin, pas de
 * niveau de natation, pas de réponse sur l'image. Appliquer telles quelles les
 * règles du parent empêcherait l'équipe de corriger une adresse tant qu'elle
 * n'a pas inventé un médecin. La règle est donc :
 *
 * - **bloquant** : ce que la modification a introduit (un prénom effacé, une
 *   allergie cochée sans la décrire, un registre national retapé faux) ;
 * - **signalé** : ce qui manquait déjà et n'a pas bougé — le parent le
 *   complétera à sa prochaine vérification de la fiche.
 *
 * Deux exceptions. La classe n'est jamais bloquante ici : l'équipe peut ne pas
 * la connaître, et son absence ne ferme que les journées pédagogiques et le
 * parascolaire (décision B, 22/10/2026). Les autorisations d'image, elles, se
 * donnent entières : dès qu'une réponse change, les cinq doivent être données
 * et l'équipe doit dire qu'elles viennent du parent — un consentement ne
 * s'invente pas, et une question laissée sans réponse s'écrirait « non ».
 *
 * Des réponses transmises par le parent et enregistrées par l'équipe valent
 * lecture de la politique de confidentialité, dans sa version du moment : la
 * fiche du parent ne la lui redemande pas (VALIDÉ ÉCLOSION, 26/10/2026).
 */

import { USAGES, DESCRIPTIONS } from '../../../../lib/droitImage';
import {
  NIVEAUX_NATATION,
  ecrituresDeLEtape,
  erreursEtape,
  versLignes,
  type ContexteFiche,
  type EcrituresDeLEtape,
  type Erreurs,
  type Etape,
  type Fiche,
  type LignesLues,
} from '../../../../components/forms/kid/fiche';

export type Onglet = Exclude<Etape, 'recapitulatif'>;

/** Les onglets de l'écran : les étapes du formulaire des parents, dans le même ordre. */
export const ONGLETS: { cle: Onglet; titre: string }[] = [
  { cle: 'identite', titre: 'Identité et école' },
  { cle: 'sante', titre: 'Santé et alimentation' },
  { cle: 'vie', titre: 'Journée en stage' },
  { cle: 'sorties', titre: 'Sorties' },
  { cle: 'autorisations', titre: 'Autorisations et RGPD' },
];

/** Ce que seule l'équipe renseigne, hors des questions posées au parent. */
export interface ReperesEquipe {
  /** `kid_health.medication_form_sent`. Repasse à « non » quand le traitement change. */
  formulaireMedication: boolean;
}

const pareil = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * La fiche à enregistrer pour un onglet : ses réponses à lui, et pour le reste
 * ce qui est en base. Un onglet modifié mais pas enregistré ne part jamais avec
 * un autre — l'accord de soins, posé dans les autorisations, réécrit la ligne
 * santé : il ne doit pas emporter une santé en cours de saisie.
 */
export function ficheDeLOnglet(onglet: Onglet, brouillon: Fiche, depart: Fiche): Fiche {
  return { ...depart, [onglet]: brouillon[onglet] };
}

/** Vrai si l'onglet a des réponses qui ne sont pas encore enregistrées. */
export function ongletModifie(
  onglet: Onglet,
  brouillon: Fiche,
  depart: Fiche,
  reperes?: ReperesEquipe,
  reperesDepart?: ReperesEquipe,
): boolean {
  if (!pareil(brouillon[onglet], depart[onglet])) return true;
  return onglet === 'sante' && !!reperes && !!reperesDepart && !pareil(reperes, reperesDepart);
}

/** La valeur d'un champ nommé par une règle : dans la partie, ou dans ses autorisations d'image. */
function valeurDuChamp(partie: unknown, cle: string): unknown {
  const p = partie as Record<string, unknown>;
  if (cle in p) return p[cle];
  const image = p.image as Record<string, unknown> | undefined;
  return image && cle in image ? image[cle] : undefined;
}

const CLES_IMAGE = new Set<string>([...USAGES, 'accordAutreParent']);

export interface ControleEquipe {
  /** Ce qui empêche d'enregistrer : introduit par la modification. */
  bloquantes: Erreurs;
  /** Ce qui manquait déjà : signalé, le parent le complétera. */
  manques: Erreurs;
}

/**
 * Les règles du parent, lues pour l'équipe (voir l'en-tête).
 *
 * `reponsesDuParent` : l'équipe confirme que les autorisations d'image qu'elle
 * enregistre lui ont été transmises par le parent.
 */
export function controleEquipe(
  onglet: Onglet,
  brouillon: Fiche,
  depart: Fiche,
  contexte: ContexteFiche = {},
  options: { reponsesDuParent?: boolean } = {},
): ControleEquipe {
  const apres = erreursEtape(onglet, ficheDeLOnglet(onglet, brouillon, depart), contexte);
  const avant = erreursEtape(onglet, depart, contexte);
  const controle: ControleEquipe = { bloquantes: {}, manques: {} };
  const imageChange =
    onglet === 'autorisations' && !pareil(brouillon.autorisations.image, depart.autorisations.image);

  for (const [cle, message] of Object.entries(apres)) {
    /*
      La lecture de la politique n'est pas une case de l'équipe : elle suit les
      autorisations d'image, dont l'enregistrement la vaut (voir l'en-tête).
    */
    if (cle === 'politiqueLue') continue;
    let bloquante: boolean;
    if (cle === 'classeId') bloquante = false;
    else if (CLES_IMAGE.has(cle)) bloquante = imageChange;
    else
      bloquante =
        !(cle in avant) || !pareil(valeurDuChamp(brouillon[onglet], cle), valeurDuChamp(depart[onglet], cle));
    (bloquante ? controle.bloquantes : controle.manques)[cle] = message;
  }

  if (imageChange && !options.reponsesDuParent) {
    controle.bloquantes.reponsesDuParent = 'Confirmez que ces réponses vous ont été transmises par le parent.';
  }
  return controle;
}

/**
 * Ce qu'il faut écrire pour enregistrer un onglet : `ecrituresDeLEtape` (rien
 * que ce qui a changé, jamais `last_verified_at`), et deux ajustements.
 *
 * - **Un niveau de natation non choisi ne réécrit pas l'ancienne réponse.**
 *   Les fiches anciennes portent un niveau libre (« moyen ») que le formulaire
 *   ne sait pas lire : corriger une restriction ne doit pas l'effacer, ni
 *   remettre `can_swim` à faux.
 * - **Le formulaire de médication** est un repère de l'équipe : son choix
 *   l'emporte, et une ligne santé entière est écrite s'il est seul à changer.
 */
export function ecrituresEquipe(
  onglet: Onglet,
  brouillon: Fiche,
  depart: Fiche,
  reperes: ReperesEquipe,
  reperesDepart: ReperesEquipe,
  kidId: string,
  auteurId: string | null,
  versionPolitique: string,
): EcrituresDeLEtape {
  const fiche = ficheDeLOnglet(onglet, brouillon, depart);
  const ecritures = ecrituresDeLEtape(onglet, fiche, depart, kidId, auteurId, versionPolitique);

  if (onglet === 'vie' && !fiche.vie.natation) {
    ecritures.lignes = ecritures.lignes.map((l) => {
      if (l.table !== 'kid_activities') return l;
      const { swim_level: _niveau, can_swim: _nage, ...reste } = l.ligne;
      void _niveau;
      void _nage;
      return { table: l.table, ligne: reste };
    });
  }

  if (onglet === 'sante' && reperes.formulaireMedication !== reperesDepart.formulaireMedication) {
    const i = ecritures.lignes.findIndex((l) => l.table === 'kid_health');
    if (i >= 0) {
      const l = ecritures.lignes[i];
      ecritures.lignes[i] = { table: l.table, ligne: { ...l.ligne, medication_form_sent: reperes.formulaireMedication } };
    } else {
      const sante = versLignes(fiche, kidId, auteurId, versionPolitique).kid_health;
      ecritures.lignes.unshift({
        table: 'kid_health',
        ligne: { ...sante, medication_form_sent: reperes.formulaireMedication },
      });
    }
  }

  return ecritures;
}

/**
 * Après l'enregistrement d'un onglet, la fiche relue : l'onglet enregistré la
 * reprend, les autres gardent ce qui y est en cours de saisie, et reprennent
 * la base là où rien n'avait été touché.
 */
export function apresEnregistrement(brouillon: Fiche, ancienDepart: Fiche, nouveauDepart: Fiche, onglet: Onglet): Fiche {
  const fusion = { ...nouveauDepart };
  for (const { cle } of ONGLETS) {
    if (cle === onglet) continue;
    if (!pareil(brouillon[cle], ancienDepart[cle])) {
      (fusion as Record<Onglet, unknown>)[cle] = brouillon[cle];
    }
  }
  return fusion;
}

/** Une heure telle que le formulaire l'écrit (« 16:30 »). */
const EST_HEURE = /^\d{2}:\d{2}(:\d{2})?$/;

/**
 * Les réponses d'avant le formulaire des parents, que l'écran ne sait pas
 * montrer dans ses champs et qu'il affiche à côté, pour que l'équipe ne les
 * croie pas perdues.
 */
export interface AnciennesReponses {
  /** Un niveau de natation libre (« moyen »), avec l'ancien « sait nager ». */
  natation: { niveau: string | null; saitNager: boolean } | null;
  /** Une heure de départ libre (« 16h30 »). */
  heureDepart: string | null;
  /** Un accompagnement dédié libre (« oui, à la piscine »). */
  accompagnement: string | null;
  /** L'ancien accord global, quand le parent n'a pas encore répondu aux cinq questions. */
  photoConsent: boolean | null;
}

export function anciennesReponses(l: LignesLues): AnciennesReponses {
  const niveau = l.activities?.swim_level ?? null;
  const niveauConnu = !!niveau && (NIVEAUX_NATATION as string[]).includes(niveau);
  const heure = l.departure?.departure_time?.trim() ?? '';
  const accompagnement = l.inclusion?.needs_dedicated_staff?.trim() ?? '';
  return {
    natation:
      niveauConnu || (!niveau?.trim() && !l.activities?.can_swim)
        ? null
        : { niveau: niveau?.trim() || null, saitNager: !!l.activities?.can_swim },
    heureDepart: heure && !EST_HEURE.test(heure) ? heure : null,
    accompagnement: accompagnement && !['yes', 'no', 'sometimes'].includes(accompagnement) ? accompagnement : null,
    photoConsent: l.image_rights ? null : (l.kids?.photo_consent ?? null),
  };
}

/** Le nom d'une autorisation dans le journal des consentements (`kid_consent_events.scope`). */
const SCOPE_VERS_USAGE: Record<string, string> = {
  internal_spaces: 'espacesPrives',
  activity_reports: 'rapports',
  public_website: 'siteInternet',
  social_media: 'reseauxSociaux',
  print_materials: 'supportsImprimes',
};

export function libelleConsentement(scope: string): string {
  if (scope === 'other_parent_agreement') return 'Accord de l’autre parent';
  const d = DESCRIPTIONS.find((x) => x.cle === SCOPE_VERS_USAGE[scope]);
  return d ? `${d.lettre}. ${d.titre}` : scope;
}
